import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import GuidedDemoForm from '@/components/GuidedDemoForm';
import { BRAND, APP_NAME, logoForTheme } from '@/lib/brand';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  ArrowRight, BarChart3, Bot, Building2, Car, Check, ChevronRight, GitBranch,
  Globe, GraduationCap, Headphones, HeartPulse, Inbox, Layers, Mail, Moon,
  Menu, Pause, Play, Store, Sun, User, UserPlus, Users, Wand2, Workflow, X,
  // galería de módulos (Fase 1)
  PhoneIncoming, PhoneOutgoing, AudioLines, PhoneCall, Radar, MessageCircle,
  LayoutTemplate, Facebook, Webhook, Package, Home, Map, Wrench, Tags, Library,
  CalendarCheck, Shuffle, Megaphone, RadioTower, Waypoints, Contact, Columns3,
  ListTodo, TrendingUp, BadgeDollarSign, Compass, FileBarChart, CreditCard,
  LayoutDashboard,
} from 'lucide-react';
import Reveal from './landing/components/Reveal';
import InstallButtons from './landing/components/InstallButtons';
import PlanRecommenderWizard from './landing/components/PlanRecommenderWizard';
import SalesContactDialog from './landing/components/SalesContactDialog';
import HeroChat from './landing/components/HeroChat';
import SeoJsonLd from './landing/components/SeoJsonLd';
import LeakCalculator from './landing/components/LeakCalculator';
import { tokens } from './landing/theme';
import { Icon } from './landing/icons';
import GirosDiagram from './landing/components/GirosDiagram';
import VozIA from './landing/components/VozIA';
import CircuitNet from './landing/components/CircuitNet';
import CookieBanner from './landing/components/CookieBanner';
import {
  CASES,
  EXTERNAL_URLS,
  FAQS,
  MODULE_CATEGORIES,
  MODULES,
  PILLARS,
  PROOF_METRICS,
  SOLO_LEADCEM,
  STEPS,
  VERTICALS,
} from './landing/data';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1556745757-8d76bdb6984b?auto=format&fit=crop&w=2000&q=80';
// Hero dinámico (Fase 5): palabras que rotan tras "Deja de tirar el dinero de tu ___"
const ROT_WORDS = ['publicidad', 'ads de Meta', 'clics de Google', 'TikTok', 'llamadas', 'WhatsApp'];
const PHONE_IMAGE = 'https://images.pexels.com/photos/7413913/pexels-photo-7413913.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1200';
const PODCAST_URL = `${process.env.REACT_APP_BACKEND_URL}/api/public/podcast/dealider`;

const ACCENT_BG = {
  cyan: 'bg-cyan-500', violet: 'bg-violet-500', emerald: 'bg-emerald-500',
  amber: 'bg-amber-500', sky: 'bg-sky-500', rose: 'bg-rose-500',
  teal: 'bg-teal-500', indigo: 'bg-indigo-500', fuchsia: 'bg-fuchsia-500',
  lime: 'bg-lime-500', navy: 'bg-[#1c3f74]', slate: 'bg-slate-800',
};

// color del chip de ícono por categoría de módulo (galería Fase 1)
const CAT_ACCENT = MODULE_CATEGORIES.reduce((acc, c) => { acc[c.key] = c.accent; return acc; }, {});

function formatTime(value) {
  const total = Number(value || 0);
  const mins = Math.floor(total / 60);
  const secs = Math.floor(total % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

export default function LandingPage() {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [dynamicPlans, setDynamicPlans] = useState([]);
  const [planTypeFilter, setPlanTypeFilter] = useState('all'); // vertical-neutral: abre en "Todos los negocios"
  const [plansLoading, setPlansLoading] = useState(false);
  const [moduleCat, setModuleCat] = useState('all'); // filtro galería de módulos (Fase 1)
  const [menuOpen, setMenuOpen] = useState(false); // menú móvil (Fase 1)
  const [rotIdx, setRotIdx] = useState(0); // hero: palabra rotativa (Fase 5)
  const [recommenderOpen, setRecommenderOpen] = useState(false);
  const [salesContactOpen, setSalesContactOpen] = useState(false);
  const [salesContactCtx, setSalesContactCtx] = useState({});
  const [podcastPlaying, setPodcastPlaying] = useState(false);
  const [podcastCurrent, setPodcastCurrent] = useState(0);
  const [podcastDuration, setPodcastDuration] = useState(0);
  const podcastRef = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;
  const t = tokens(theme);

  // Cargar planes dinámicos cuando cambia el filtro de vertical
  useEffect(() => {
    setPlansLoading(true);
    const params = planTypeFilter !== 'all' ? `?type=${planTypeFilter}` : '';
    fetch(`${API}/api/plans/public${params}`)
      .then(r => r.json())
      .then(data => setDynamicPlans(Array.isArray(data) ? data : []))
      .catch(() => setDynamicPlans([]))
      .finally(() => setPlansLoading(false));
  }, [API, planTypeFilter]);

  // Hero: rota la palabra tras "Deja de tirar el dinero de tu ___" (Fase 5)
  useEffect(() => {
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce) return;
    const id = setInterval(() => setRotIdx((i) => (i + 1) % ROT_WORDS.length), 2400);
    return () => clearInterval(id);
  }, []);

  // Wire del audio del podcast
  useEffect(() => {
    const audio = podcastRef.current;
    if (!audio) return;
    const onTime = () => setPodcastCurrent(audio.currentTime || 0);
    const onMeta = () => setPodcastDuration(audio.duration || 0);
    const onEnd = () => setPodcastPlaying(false);
    audio.addEventListener('timeupdate', onTime);
    audio.addEventListener('loadedmetadata', onMeta);
    audio.addEventListener('ended', onEnd);
    return () => {
      audio.removeEventListener('timeupdate', onTime);
      audio.removeEventListener('loadedmetadata', onMeta);
      audio.removeEventListener('ended', onEnd);
    };
  }, []);

  const togglePodcast = async () => {
    const audio = podcastRef.current;
    if (!audio) return;
    if (podcastPlaying) {
      audio.pause();
      setPodcastPlaying(false);
      return;
    }
    try {
      await audio.play();
      setPodcastPlaying(true);
    } catch {
      setPodcastPlaying(false);
    }
  };

  const podcastProgress = useMemo(() => {
    if (!podcastDuration) return 0;
    return Math.min(100, (podcastCurrent / podcastDuration) * 100);
  }, [podcastCurrent, podcastDuration]);

  return (
    <div className={`min-h-screen ${t.page} overflow-x-hidden transition-colors duration-300`} data-testid="landing-page">
      <SeoJsonLd />

      {/* ─── Navbar (responsivo + día/noche) ─── */}
      <nav className={`fixed top-0 left-0 right-0 z-50 ${t.nav} transition-colors`} data-testid="landing-navbar">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-4">
          <a href="#top" aria-label="LeadCEM"><img src={logoForTheme(theme)} alt="LEADCEM" className="h-8 sm:h-9 w-auto object-contain" data-testid="landing-logo" /></a>
          <div className="hidden lg:flex items-center gap-7 text-sm">
            <a href="#solo-leadcem" className={`font-semibold ${t.navLink} transition-colors`}>Módulos</a>
            <a href="#verticales" className={`font-semibold ${t.navLink} transition-colors`}>Verticales</a>
            <a href="#voz-ia" className={`font-semibold ${t.navLink} transition-colors`}>Voz IA</a>
            <a href="#precios" className={`font-semibold ${t.navLink} transition-colors`}>Planes</a>
            <button onClick={toggleTheme} className={t.textFaint} data-testid="landing-theme-toggle" aria-label="Cambiar tema día/noche">
              {t.dark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            {user ? (
              <Button onClick={() => navigate('/dashboard')} className="bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full h-10 font-semibold" data-testid="landing-go-dashboard">Dashboard <ChevronRight className="w-4 h-4 ml-1" /></Button>
            ) : (
              <>
                <Link to="/login"><Button variant="ghost" className={`${t.textSub} h-10`} data-testid="landing-login-btn">Iniciar sesión</Button></Link>
                <a href={EXTERNAL_URLS.whatsapp} target="_blank" rel="noopener noreferrer"><Button className="lc-shine bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full h-10 font-semibold" data-testid="landing-register-btn">Probar la IA</Button></a>
              </>
            )}
          </div>
          <div className="flex lg:hidden items-center gap-1">
            <button onClick={toggleTheme} className={`p-2 ${t.textFaint}`} aria-label="Cambiar tema">{t.dark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}</button>
            <button onClick={() => setMenuOpen((v) => !v)} className={`p-2 ${t.text}`} aria-label="Menú" data-testid="landing-menu-toggle">{menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}</button>
          </div>
        </div>
        {menuOpen && (
          <div className={`lg:hidden ${t.sectionA} border-t ${t.dark ? 'border-white/10' : 'border-slate-200'} px-4 py-3 flex flex-col gap-1`} data-testid="landing-mobile-menu">
            {[['#solo-leadcem', 'Módulos'], ['#verticales', 'Verticales'], ['#voz-ia', 'Voz IA'], ['#precios', 'Planes']].map(([href, label]) => (
              <a key={href} href={href} onClick={() => setMenuOpen(false)} className={`py-2.5 font-semibold ${t.navLink}`}>{label}</a>
            ))}
            <div className="flex flex-col gap-2 pt-2">
              <a href={EXTERNAL_URLS.whatsapp} target="_blank" rel="noopener noreferrer"><Button className="w-full bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full h-11 font-semibold">💬 Probar por WhatsApp</Button></a>
              {!user && <Link to="/login"><Button variant="ghost" className={`w-full ${t.textSub}`}>Iniciar sesión</Button></Link>}
            </div>
          </div>
        )}
      </nav>

      {/* ─── Hero ─────────────────────────────────────────────── */}
      <section className={`relative min-h-[88vh] flex items-center overflow-hidden ${t.dark ? 'bg-[#0a1424]' : 'bg-gradient-to-b from-[#eef3fb] to-white'}`} data-testid="hero-section">
        <div className={`absolute inset-0 ${t.glow}`} />
        <CircuitNet className="absolute inset-0 h-full w-full pointer-events-none opacity-60" />
        <style>{`
          @property --lc-angle{syntax:'<angle>';initial-value:0deg;inherits:false}
          @keyframes lcrot{from{opacity:.15}to{opacity:1}}
          @keyframes lcspin{to{--lc-angle:360deg}}
          .lc-rot{display:inline-block;animation:lcrot .3s ease}
          /* border-beam: una luz recorre el borde (cíborg/circuito) */
          .lc-beam{position:relative}
          .lc-beam::before{content:"";position:absolute;inset:0;border-radius:inherit;padding:1.5px;background:conic-gradient(from var(--lc-angle),transparent 0 60%,#7cc23e 76%,#eaffc4 85%,#7cc23e 94%,transparent 100%);-webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;mask-composite:exclude;animation:lcspin 4.5s linear infinite;pointer-events:none;z-index:1}
          /* sheen: barrido de luz al hover */
          .lc-shine{position:relative;overflow:hidden}
          .lc-shine::after{content:"";position:absolute;inset:0 auto 0 0;width:55%;background:linear-gradient(100deg,transparent,rgba(255,255,255,.5),transparent);transform:translateX(-190%);pointer-events:none}
          @media (hover:hover) and (pointer:fine){.lc-shine:hover::after{transition:transform .7s cubic-bezier(.23,1,.32,1);transform:translateX(360%)}}
          @media (prefers-reduced-motion:reduce){.lc-beam::before{animation:none;opacity:.55}.lc-shine::after{display:none}}
        `}</style>

        <div className="relative z-[1] max-w-7xl mx-auto w-full px-6 lg:px-8 pt-24 pb-16 grid lg:grid-cols-[1.1fr_0.9fr] gap-12 items-center">
          <Reveal className="space-y-8">
            <Badge className={`rounded-full uppercase tracking-[0.2em] text-[10px] px-3 py-1 border ${t.dark ? 'bg-white/10 text-white/90 border-white/15' : 'bg-[#1c3f74]/10 text-[#1c3f74] border-[#1c3f74]/20'}`}>
              SaaS comercial con IA sincronizada
            </Badge>
            <div className="space-y-5">
              <h1 className={`text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1.02] ${t.text}`} data-testid="hero-main-headline">
                Deja de tirar el dinero de tu{' '}
                <span key={rotIdx} className={`lc-rot whitespace-nowrap border-b-2 ${t.dark ? 'border-lime-400/30' : 'border-lime-500/30'} ${t.accentText}`} data-testid="hero-rotword">{ROT_WORDS[rotIdx]}</span>.
              </h1>
              <p className={`text-base md:text-lg ${t.textSub} max-w-2xl leading-relaxed`} data-testid="hero-main-subheadline">
                {APP_NAME} contesta en segundos cada lead que <b className={t.text}>ya pagaste</b> —por WhatsApp y voz—,
                lo califica, agenda la cita y da seguimiento 24/7 hasta que cierran. Para inmobiliaria, autos, educación, comercio y más.
              </p>
            </div>

            <div className="flex flex-wrap gap-2">
              <span className={`text-[12px] rounded-full px-3 py-1.5 ${t.surface} ${t.textSub}`}>⚡ Responde en segundos</span>
              <span className={`text-[12px] rounded-full px-3 py-1.5 ${t.surface} ${t.textSub}`}>🔁 Seguimiento 24/7</span>
              <span className={`text-[12px] rounded-full px-3 py-1.5 ${t.surface} ${t.textSub}`}>🧩 +30 módulos</span>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <a href={EXTERNAL_URLS.whatsapp} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
                <Button className="lc-shine w-full sm:w-auto bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full px-8 h-12 font-semibold shadow-lg shadow-lime-400/30" data-testid="hero-cta-primary">
                  💬 Pruébala por WhatsApp <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </a>
              <Button
                onClick={() => setRecommenderOpen(true)}
                variant="outline"
                className={`w-full sm:w-auto border-2 bg-transparent rounded-full px-8 h-12 font-semibold ${t.dark ? 'border-lime-300/60 text-lime-200 hover:bg-lime-300/10' : 'border-[#4a8c00]/50 text-[#4a8c00] hover:bg-[#f0f7e2]'}`}
                data-testid="hero-cta-recommender"
              >
                Encuentra tu plan ideal
              </Button>
            </div>
            <p className={`${t.textFaint} text-xs`}>o <Link to="/login" className={`underline ${t.accentText}`}>empezar la prueba guiada</Link> — escríbele a la IA como si fueras cliente.</p>

            <InstallButtons variant={t.dark ? 'dark' : 'light'} />

            <div className={`max-w-2xl rounded-2xl p-4 ${t.glass} lc-beam`} data-testid="landing-guided-demo-card">
              <GuidedDemoForm
                origin="landing_principal"
                title="Regístrate sin contraseña para una visita guiada"
                className={t.text}
              />
            </div>
          </Reveal>

          <Reveal delay={120}>
            <HeroChat />
          </Reveal>
        </div>
      </section>

      {/* ─── Calculadora de fuga (Fase 2) ────────────────────── */}
      <LeakCalculator />

      {/* ─── Cómo funciona en 3 pasos (Fase 2) ───────────────── */}
      <section id="como-funciona" className={`py-20 md:py-24 ${t.sectionB}`} data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 md:space-y-12">
          <Reveal className="text-center max-w-3xl mx-auto">
            <p className={`text-xs md:text-sm uppercase tracking-[0.2em] font-bold ${t.textFaint}`}>Cómo funciona</p>
            <h2 className={`text-2xl md:text-4xl lg:text-5xl font-semibold mt-4 ${t.text}`}>De un clic al cierre, en 3 pasos.</h2>
            <p className={`text-sm md:text-base ${t.textSub} mt-4`}>Sin instalar nada raro ni cambiar cómo trabajas. Lo enciendes, y la IA empieza a contestar.</p>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {STEPS.map((step, idx) => (
              <Reveal key={step.n} delay={idx * 80} className={`rounded-2xl p-8 ${t.surface} relative overflow-hidden`}>
                <div className="flex items-center gap-4 mb-5">
                  <div className={`w-12 h-12 rounded-xl ${t.iconChipNavy} flex items-center justify-center shrink-0`}>
                    <Icon name={step.icon} className="w-5 h-5" />
                  </div>
                  <span className={`text-5xl font-bold leading-none ${t.dark ? 'text-white/10' : 'text-slate-200'}`} aria-hidden="true">{step.n}</span>
                </div>
                <h3 className={`text-xl font-semibold mb-3 ${t.text}`}>{step.title}</h3>
                <p className={`text-sm ${t.textSub} leading-relaxed`}>{step.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Social Proof Metrics ────────────────────────────── */}
      <section className={`py-20 ${t.sectionA}`} data-testid="social-proof-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5">
          {PROOF_METRICS.map(([value, label], idx) => (
            <Reveal key={value} delay={idx * 60} className={`rounded-2xl p-5 sm:p-6 ${t.surface}`}>
              <p className={`text-3xl sm:text-4xl font-semibold ${t.accentText}`} data-testid={`proof-metric-value-${idx}`}>{value}</p>
              <p className={`text-xs sm:text-sm ${t.textSub} mt-2`} data-testid={`proof-metric-label-${idx}`}>{label}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* ─── Ecosistema (3 pilares) ──────────────────────────── */}
      <section id="ecosistema" className={`py-20 md:py-24 ${t.sectionB}`} data-testid="ecosystem-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10 md:space-y-12">
          <Reveal className="text-center max-w-3xl mx-auto">
            <p className={`text-xs md:text-sm uppercase tracking-[0.2em] font-bold ${t.textFaint}`}>Ecosistema {APP_NAME}</p>
            <h2 className={`text-2xl md:text-4xl lg:text-5xl font-semibold mt-4 ${t.text}`}>Tu operación comercial completa en una sola plataforma.</h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-5">
            {PILLARS.map((pillar, idx) => (
              <Reveal key={pillar.title} delay={idx * 80} className={`rounded-2xl p-8 ${t.surface}`}>
                <div className={`w-12 h-12 rounded-xl ${t.iconChipNavy} flex items-center justify-center mb-5`}>
                  <Icon name={pillar.icon} className="w-5 h-5" />
                </div>
                <h3 className={`text-2xl font-semibold mb-3 ${t.text}`}>{pillar.title}</h3>
                <p className={`text-sm ${t.textSub} leading-relaxed`}>{pillar.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Verticales soportadas ───────────────────────────── */}
      <section id="verticales" className={`py-20 md:py-24 ${t.sectionA}`} data-testid="verticals-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <Reveal className="text-center max-w-3xl mx-auto">
            <p className={`text-xs md:text-sm uppercase tracking-[0.2em] font-bold ${t.textFaint}`}>Elige tu giro</p>
            <h2 className={`text-2xl md:text-4xl lg:text-5xl font-semibold mt-4 ${t.text}`}>Si vendes hablando con gente, LeadCEM es para ti.</h2>
            <p className={`text-sm md:text-base ${t.textSub} mt-4`}>Elige tu tipo de negocio y mira exactamente qué módulos se encienden para ti. Somos especialistas en varios giros —y en el tuyo también.</p>
          </Reveal>

          <GirosDiagram />

          <div className="grid sm:grid-cols-2 gap-4" data-testid="vertical-cases-grid">
            {CASES.map((c, idx) => (
              <Reveal key={c.vertical} delay={idx * 60} className={`rounded-2xl p-6 ${t.surface}`}>
                <p className={`text-sm font-semibold ${t.accentText}`}>Caso: {c.vertical}</p>
                <p className={`text-sm ${t.textSub} mt-2`}>{c.headline}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Podcast eliminado (no está en el mockup — "quitar lo que no va") ─── */}

      {/* ─── 7a · Solo LeadCEM (día/noche por tokens) ─── */}
      <section id="solo-leadcem" className={`py-20 md:py-24 ${t.sectionB} relative overflow-hidden`} data-testid="solo-leadcem-section">
        <div className={`absolute inset-0 pointer-events-none ${t.glow}`} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <Reveal className="text-center max-w-3xl mx-auto">
            <p className={`text-xs md:text-sm uppercase tracking-[0.2em] font-bold ${t.accentText}`}>Solo LeadCEM</p>
            <h2 className={`text-2xl md:text-4xl lg:text-5xl font-semibold mt-4 ${t.text}`}>Esto no lo vas a encontrar en un CRM ni en un chatbot.</h2>
            <p className={`text-sm md:text-base ${t.textSub} mt-4`}>CEM + CRM + administrador de tareas, omnicanal, con la IA usando tus herramientas reales.</p>
          </Reveal>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
            {SOLO_LEADCEM.map((item, idx) => (
              <Reveal key={item.title} delay={idx * 50} className={`rounded-2xl p-6 sm:p-8 ${t.surface} ${t.surfaceHover} transition-all`} data-testid={`solo-card-${idx}`}>
                <div className={`w-12 h-12 rounded-xl ${t.iconChip} flex items-center justify-center mb-5`}>
                  <Icon name={item.icon} className="w-5 h-5" />
                </div>
                <h3 className={`text-lg font-semibold mb-3 ${t.text}`}>{item.title}</h3>
                <p className={`text-sm ${t.textSub} leading-relaxed`}>{item.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── 7b · Galería de +30 módulos (día/noche por tokens) ─── */}
      <section id="funciones" className={`py-20 md:py-24 ${t.sectionA} relative overflow-hidden`} data-testid="modules-gallery-section">
        <div className={`absolute inset-0 pointer-events-none ${t.glow}`} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <Reveal className="text-center max-w-3xl mx-auto">
            <p className={`text-xs md:text-sm uppercase tracking-[0.2em] font-bold ${t.textFaint}`}>Todo el sistema</p>
            <h2 className={`text-2xl md:text-4xl lg:text-5xl font-semibold mt-4 ${t.text}`}>No te lo decimos con adjetivos. Te lo probamos con <span className={t.accentText}>+30 módulos</span>.</h2>
            <p className={`text-sm md:text-base ${t.textSub} mt-4`}>Capta, vende y da seguimiento en una sola plataforma. Filtra por lo que te interesa.</p>
          </Reveal>
          <Reveal className="flex justify-center">
            <div className={`inline-flex flex-wrap justify-center gap-1 p-1 rounded-full ${t.surface}`} data-testid="module-cat-filter">
              {[{ key: 'all', label: 'Todos' }, ...MODULE_CATEGORIES].map((c) => (
                <button
                  key={c.key}
                  onClick={() => setModuleCat(c.key)}
                  className={`px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium transition-colors ${moduleCat === c.key ? 'bg-lime-400 text-slate-950' : t.textSub}`}
                  data-testid={`module-cat-${c.key}`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-5">
            {MODULES.filter((m) => moduleCat === 'all' || m.category === moduleCat).map((m, idx) => (
              <Reveal key={m.key} delay={(idx % 6) * 40} className={`rounded-2xl p-5 sm:p-6 ${t.surface} ${t.surfaceHover} transition-all`} data-testid={`module-card-${m.key}`}>
                <div className={`w-11 h-11 rounded-xl ${t.iconChip} flex items-center justify-center mb-4`}>
                  <Icon name={m.icon} className="w-5 h-5" />
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className={`text-base font-semibold ${t.text}`}>{m.name}</h3>
                  {m.only_leadcem && <Badge className="bg-lime-400/15 text-lime-600 border-0 text-[10px]">Solo LeadCEM</Badge>}
                </div>
                <p className={`text-sm ${t.textSub} leading-relaxed mt-2`}>{m.context}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Voz IA (Fase 4) — bloque navy, diferenciador de voz ─── */}
      <VozIA />

      {/* ─── Planes dinámicos (conectado a /api/plans/public · día/noche) ─── */}
      <section className={`py-20 md:py-24 ${t.sectionB}`} id="precios" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          <Reveal className="text-center max-w-3xl mx-auto">
            <p className={`text-xs md:text-sm uppercase tracking-[0.2em] font-bold ${t.textFaint}`}>Planes</p>
            <h2 className={`text-2xl md:text-4xl lg:text-5xl font-semibold mt-4 ${t.text}`}>No elijas el más barato. Elige el que más dinero te devuelve.</h2>
            <p className={`text-sm md:text-base ${t.textSub} mt-4`}>Cada plan está pensado para tu giro y el tamaño de tu operación. ¿Dudas? La IA te recomienda el ideal en 1 minuto.</p>
            <Button
              onClick={() => setRecommenderOpen(true)}
              className="mt-6 bg-lime-400 hover:bg-lime-300 text-slate-950 font-bold rounded-full px-7 h-11 shadow-lg shadow-lime-400/30"
              data-testid="pricing-recommender-cta"
            >
              Encuentra tu plan ideal <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Reveal>

          <Reveal className="flex justify-center">
            <div className={`inline-flex flex-wrap justify-center gap-1 p-1 rounded-full ${t.surface}`} data-testid="plan-type-filter">
              {VERTICALS.map(v => (
                <button
                  key={v.key}
                  onClick={() => setPlanTypeFilter(v.key === 'all' ? 'all' : v.plan_filter)}
                  className={`px-3 sm:px-4 py-2 text-xs font-medium rounded-full transition-colors ${
                    (planTypeFilter === 'all' && v.key === 'all') || planTypeFilter === v.plan_filter
                      ? 'bg-lime-400 text-slate-950'
                      : t.textSub
                  }`}
                  data-testid={`plan-filter-${v.key}`}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </Reveal>

          {/* Loading state */}
          {plansLoading && (
            <Reveal className={`text-center ${t.textSub} text-sm py-8`} data-testid="plans-loading">
              Cargando planes…
            </Reveal>
          )}

          {/* Empty state (giro sin planes activos) — honesto */}
          {!plansLoading && dynamicPlans.length === 0 && (
            <Reveal className="text-center max-w-xl mx-auto py-12" data-testid="plans-empty">
              <p className={`text-base ${t.text}`}>Estamos afinando los planes de este giro.</p>
              <p className={`text-sm ${t.textSub} mt-2`}>Escríbele a la IA por WhatsApp y te armamos un plan a tu medida hoy mismo.</p>
              <a href={EXTERNAL_URLS.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-block mt-6">
                <Button className="bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full px-8 h-11 font-semibold">💬 Escríbele a la IA</Button>
              </a>
            </Reveal>
          )}

          {/* Plan cards */}
          {!plansLoading && dynamicPlans.length > 0 && (
            <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
              {dynamicPlans.map((plan, idx) => (
                <Reveal key={plan.id} delay={idx * 60} className={`rounded-2xl relative ${plan.is_popular ? `border-2 border-lime-400 ${t.dark ? 'bg-lime-400/[0.06]' : 'bg-lime-50'}` : t.surface}`} data-testid={`plan-card-${plan.id}`}>
                  {plan.is_popular && <div className="absolute top-0 right-0 px-3 py-1 bg-lime-400 text-slate-950 text-[10px] font-bold rounded-bl-xl rounded-tr-2xl">POPULAR</div>}
                  <div className="p-6 sm:p-7 space-y-5">
                    <div>
                      <p className={`text-xl font-semibold ${t.text}`}>{plan.name}</p>
                      <p className={`text-sm ${t.textSub} mt-2 min-h-[40px]`}>{plan.description}</p>
                    </div>
                    <div>
                      {plan.is_sas_only ? (
                        <span className={`text-xl font-semibold ${t.accentText}`}>Cotización personalizada</span>
                      ) : plan.price > 0 ? (
                        <>
                          <span className={`text-4xl font-semibold ${t.text}`}>${plan.price}</span>
                          <span className={`text-sm ${t.textFaint} ml-2`}>/{plan.billing_period === 'month' ? 'mes' : plan.billing_period}</span>
                        </>
                      ) : (
                        <span className={`text-2xl font-semibold ${t.accentText}`}>Cotización personalizada</span>
                      )}
                    </div>
                    <div className="space-y-2">
                      {(plan.features || []).slice(0, 6).map((f, fi) => (
                        <div key={fi} className={`flex items-start gap-2 text-sm ${t.textSub}`} data-testid={`plan-feature-${plan.id}-${fi}`}>
                          <Check className="w-4 h-4 text-lime-500 mt-0.5 shrink-0" />
                          {f}
                        </div>
                      ))}
                    </div>
                    {plan.landing && (
                      <div className="space-y-2 pt-1" data-testid={`plan-landing-${plan.id}`}>
                        <p className={`text-xs ${t.textFaint}`} data-testid={`plan-leads-cap-${plan.id}`}>
                          {Number(plan.landing.caps?.leads_mes) >= 0
                            ? `${plan.landing.caps.leads_mes} leads/mes`
                            : 'Leads ilimitados'}
                        </p>
                        <div className="flex flex-wrap gap-1">
                          {(plan.landing.modulos || []).slice(0, 8).map((m) => (
                            <span key={m.key} className={`text-[10px] px-1.5 py-0.5 rounded ${t.dark ? 'bg-white/10 text-white/80' : 'bg-slate-100 text-slate-700'}`}>{m.label}</span>
                          ))}
                        </div>
                        {plan.landing.vende_voz ? (
                          <p className={`text-[10px] ${t.accentText}`} data-testid={`plan-voz-${plan.id}`}>
                            {(plan.landing.grado_automatizacion || []).join(' · ')}
                          </p>
                        ) : (
                          <p className={`text-[10px] ${t.textFaint}`} data-testid={`plan-sin-voz-${plan.id}`}>Sin voz</p>
                        )}
                      </div>
                    )}
                    {plan.is_sas_only ? (
                      <Button
                        onClick={() => { setSalesContactCtx({ plan_id: plan.id, industry: planTypeFilter !== 'all' ? planTypeFilter : '' }); setSalesContactOpen(true); }}
                        className="w-full rounded-full bg-lime-400 hover:bg-lime-300 text-slate-950 font-bold"
                        data-testid={`plan-cta-${plan.id}`}
                      >
                        Contactar a ventas
                      </Button>
                    ) : (
                      <Link to="/login">
                        <Button className={`w-full rounded-full font-semibold ${plan.is_popular ? 'bg-lime-400 hover:bg-lime-300 text-slate-950' : `${t.iconChipNavy} hover:opacity-90`}`} data-testid={`plan-cta-${plan.id}`}>
                          {plan.price > 0 ? 'Empezar prueba guiada' : 'Solicitar cotización'}
                        </Button>
                      </Link>
                    )}
                  </div>
                </Reveal>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ─── FAQ visible (Fase 5) ─── */}
      <section id="faq" className={`py-20 md:py-24 ${t.sectionA}`} data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-10">
            <p className={`text-xs md:text-sm uppercase tracking-[0.2em] font-bold ${t.textFaint}`}>Preguntas frecuentes</p>
            <h2 className={`text-2xl md:text-4xl lg:text-5xl font-semibold mt-4 ${t.text}`}>Lo que todo dueño pregunta antes de decir que sí.</h2>
          </Reveal>
          <div className="space-y-3">
            {FAQS.map((f, idx) => (
              <Reveal key={idx} delay={idx * 40}>
                <details className={`group rounded-2xl px-5 ${t.surface}`} data-testid={`faq-item-${idx}`}>
                  <summary className={`flex items-center justify-between gap-4 cursor-pointer list-none py-4 font-semibold ${t.text}`}>
                    {f.q}
                    <span className={`shrink-0 text-xl leading-none ${t.accentText} transition-transform duration-200 group-open:rotate-45`} aria-hidden="true">+</span>
                  </summary>
                  <p className={`text-sm ${t.textSub} leading-relaxed pb-5 -mt-1`}>{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Final CTA (navy + lima) ─── */}
      <section className="py-20 md:py-24 bg-gradient-to-br from-[#0f2f5c] to-[#1c3f74] text-[#ffffff] relative overflow-hidden" data-testid="final-cta">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(124,194,62,.18),transparent_50%)]" />
        <Reveal className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-7">
          <p className="text-xs md:text-sm uppercase tracking-[0.2em] font-bold text-lime-300">La única pregunta que importa</p>
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-semibold leading-tight">La pregunta no es si esto funciona. Es quién en tu giro lo va a tener primero.</h2>
          <p className="text-base text-[#ffffff]/85 max-w-2xl mx-auto">
            Mientras lo piensas, alguien que vende lo mismo que tú ya está contestando en segundos a la gente que también te escribió. Pruébalo hoy: te contesta la misma IA que atendería a tus clientes.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-3">
            <a href={EXTERNAL_URLS.whatsapp} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto"><Button className="lc-shine w-full bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full px-8 h-12 font-semibold" data-testid="final-cta-primary">💬 Pruébala por WhatsApp <ArrowRight className="w-4 h-4 ml-2" /></Button></a>
            <Link to="/login" className="w-full sm:w-auto"><Button variant="outline" className="w-full border-2 border-[#ffffff]/60 text-[#ffffff] bg-transparent hover:bg-white/10 rounded-full px-8 h-12" data-testid="final-cta-secondary">Empezar prueba guiada</Button></Link>
          </div>
          <p className="text-[#ffffff]/60 text-xs">Sin instalar nada. Sin tarjeta. Solo escríbele.</p>
          <div className="flex justify-center"><InstallButtons variant="light" /></div>
        </Reveal>
      </section>

      {/* ─── Footer real (Fase 5) ─── */}
      <footer className={`${t.sectionB} border-t ${t.dark ? 'border-white/10' : 'border-slate-200'}`} data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid gap-10 md:grid-cols-[1.6fr_1fr_1fr_1fr]">
            <div>
              <img src={logoForTheme(theme)} alt="LEADCEM" className="h-8 w-auto object-contain" />
              <p className={`text-sm ${t.textSub} mt-4 max-w-xs leading-relaxed`}>La plataforma omnicanal más completa: CEM + CRM + tareas con Agente IA que contesta, califica y da seguimiento por WhatsApp y voz, 24/7.</p>
              <a href={EXTERNAL_URLS.whatsapp} target="_blank" rel="noopener noreferrer" className="inline-block mt-5">
                <Button className="bg-lime-400 hover:bg-lime-300 text-slate-950 rounded-full h-10 px-5 font-semibold text-sm">💬 Probar la IA</Button>
              </a>
            </div>
            <div>
              <p className={`text-xs font-bold uppercase tracking-wide ${t.textFaint} mb-4`}>Producto</p>
              <ul className="space-y-2.5 text-sm">
                <li><a href="#solo-leadcem" className={t.navLink}>Módulos</a></li>
                <li><a href="#verticales" className={t.navLink}>Tu giro</a></li>
                <li><a href="#voz-ia" className={t.navLink}>Voz IA</a></li>
                <li><a href="#precios" className={t.navLink}>Planes</a></li>
              </ul>
            </div>
            <div>
              <p className={`text-xs font-bold uppercase tracking-wide ${t.textFaint} mb-4`}>Empresa</p>
              <ul className="space-y-2.5 text-sm">
                <li><a href="#faq" className={t.navLink}>Preguntas frecuentes</a></li>
                <li><Link to="/developers" className={t.navLink} data-testid="landing-api-docs-link">API · Documentación</Link></li>
                <li><Link to="/login" className={t.navLink}>Iniciar sesión</Link></li>
              </ul>
            </div>
            <div>
              <p className={`text-xs font-bold uppercase tracking-wide ${t.textFaint} mb-4`}>Legal</p>
              <ul className="space-y-2.5 text-sm">
                <li><Link to="/terminos" className={t.navLink} data-testid="landing-terminos-link">Términos y Condiciones</Link></li>
                <li><a href="/legal/privacidad.html" target="_blank" rel="noopener" className={t.navLink} data-testid="landing-privacidad-link">Aviso de Privacidad</a></li>
                <li><a href="/legal/privacidad.html" target="_blank" rel="noopener" className={t.navLink}>Política de Cookies</a></li>
              </ul>
            </div>
          </div>
          <div className={`mt-12 pt-6 border-t ${t.dark ? 'border-white/10' : 'border-slate-200'} flex flex-col sm:flex-row gap-3 justify-between ${t.textFaint} text-xs`}>
            <span>© {new Date().getFullYear()} {APP_NAME}. Operado por Dragon Technologies LLC. Todos los derechos reservados.</span>
            <span>Hecho en México 🇲🇽 · WhatsApp Oficial (API) verificado</span>
          </div>
        </div>
      </footer>

      <CookieBanner />

      <PlanRecommenderWizard
        open={recommenderOpen}
        onClose={() => setRecommenderOpen(false)}
        onPickPlan={(plan) => {
          setRecommenderOpen(false);
          setPlanTypeFilter((plan.visible_for_types || [])[0] || 'all');
          const target = document.getElementById('precios');
          if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }}
        onContactSales={(ctx) => {
          setRecommenderOpen(false);
          setSalesContactCtx(ctx || {});
          setSalesContactOpen(true);
        }}
      />
      <SalesContactDialog
        open={salesContactOpen}
        context={salesContactCtx}
        onClose={() => setSalesContactOpen(false)}
      />

      {/* Botones flotantes: Llamar IA (voz) + WhatsApp (Carlo) */}
      <div className="fixed right-4 bottom-4 z-[60] flex flex-col gap-2.5" data-testid="landing-fabs">
        <a href="tel:+525571001192" aria-label="Llamar a la IA de voz" className="flex items-center gap-2 rounded-full bg-[#1c3f74] text-white font-semibold text-sm px-4 h-11 shadow-lg shadow-black/40 hover:brightness-110 transition">📞 Llamar IA</a>
        <a href={EXTERNAL_URLS.whatsapp} target="_blank" rel="noopener noreferrer" aria-label="Probar por WhatsApp" className="flex items-center gap-2 rounded-full bg-[#25d366] text-white font-semibold text-sm px-4 h-11 shadow-lg shadow-black/40 hover:brightness-105 transition">💬 WhatsApp</a>
      </div>
    </div>
  );
}
